import { amountRoundUpWithoutCents, multiplyByRate, newAmount, newFinancialRate } from '@orus.eu/amount'

import type {
  InsuranceLimitOfIndemnity,
  LimitOfIndemnityPart,
  PossibleInsuranceLimitOfIndemnity,
} from '../../../model/policy-item.ts'
import type { FrMrgaPolicyItem, FrMrgaPolicyItemsState } from '../fr-mrga-insurance.ts'

import { frMrgaFixedLimitsOfIndemnity_v1_0_0 as fixedLimitsOfIndemnity } from './fixed-limits-of-indemnity.ts'

// fr-mrga has no limit selector, so a limit offered as a choice among several values cannot be written here.
export type FrMrgaLimitOfIndemnity = Extract<
  PossibleInsuranceLimitOfIndemnity,
  { type: 'fixedAmount' | 'none' | 'text' | 'variableAmount' | 'multipleLimits' }
>

export type FrMrgaLimitOfIndemnityEntry = { additionalInfo: string | null } & (
  | { limit: FrMrgaLimitOfIndemnity; select?: never }
  | { select: (state: FrMrgaPolicyItemsState) => InsuranceLimitOfIndemnity; limit?: never }
)

type MultipleLimits = Extract<InsuranceLimitOfIndemnity, { type: 'multipleLimits' }>

type SingleLimit = LimitOfIndemnityPart['limit']

/** The amount stays to be determined until the customer chooses it at subscription. */
function chosenAmountLimit(chosenAmount: number | null | undefined): SingleLimit {
  return chosenAmount == null
    ? { type: 'variableAmount' }
    : { type: 'calculatedAmount', value: newAmount(chosenAmount) }
}

/**
 * A share of the content capital, rounded up to the euro. The fire guarantee covers the whole capital, so a share of
 * the fire guarantee on the content is a share of the capital.
 */
function contentCapitalShare({ state, rate }: { state: FrMrgaPolicyItemsState; rate: string }): SingleLimit {
  const { frMrgaContentCapital } = state
  if (frMrgaContentCapital == null) return { type: 'variableAmount' }
  return {
    type: 'calculatedAmount',
    value: amountRoundUpWithoutCents(multiplyByRate(newAmount(frMrgaContentCapital), newFinancialRate(rate))),
  }
}

const contentCapital = (state: FrMrgaPolicyItemsState): LimitOfIndemnityPart => ({
  label: 'Contenu',
  limit: chosenAmountLimit(state.frMrgaContentCapital),
})

const fittingsFinancialLoss = (state: FrMrgaPolicyItemsState): LimitOfIndemnityPart => ({
  label: 'Perte financière sur aménagements mobiliers et immobiliers',
  limit: contentCapitalShare({ state, rate: '0.25' }),
})

const expertFeesOfDirectDamage = (percentage: number): LimitOfIndemnityPart => ({
  label: 'Honoraires d’expert',
  limit: { type: 'text', value: `${percentage} % de l’indemnité versée au titre des dommages matériels directs` },
})

const neighboursAndThirdPartiesLiability: LimitOfIndemnityPart = {
  label: 'Responsabilité à l’égard des voisins et tiers',
  limit: { type: 'text', value: '1 800 000 €, dont 10 % pour les dommages immatériels consécutifs' },
}

const tenantsRecourse = (label: string): LimitOfIndemnityPart => ({
  label,
  limit: {
    type: 'text',
    value:
      '900 000 €, dont 10 % pour les dommages immatériels consécutifs, pertes de loyer limitées à 1 année à compter de l’événement',
  },
})

const rentalRisks = (label: string): LimitOfIndemnityPart => ({
  label,
  limit: { type: 'text', value: '2 000 € par m², dans la limite de 2 000 000 €' },
})

const fireLimit = (state: FrMrgaPolicyItemsState): MultipleLimits => ({
  type: 'multipleLimits',
  limits: [
    { label: 'Bâtiment', limit: { type: 'text', value: 'Valeur de reconstruction à neuf ou montant des réparations' } },
    contentCapital(state),
    { label: 'Perte d’usage', limit: { type: 'text', value: '2 années de valeur locative maximum' } },
    expertFeesOfDirectDamage(5),
    fittingsFinancialLoss(state),
    { label: 'Perte de loyer', limit: { type: 'text', value: '2 années de loyer' } },
    {
      label: 'Frais et pertes justifiables',
      limit: { type: 'text', value: '10 % de l’indemnité versée au titre des dommages matériels directs' },
    },
    {
      label: 'Frais et pertes divers',
      limit: { type: 'text', value: '10 % de l’indemnité versée au titre des biens garantis, sur justificatif' },
    },
    rentalRisks('Responsabilité en tant que locataire à l’égard du propriétaire'),
    tenantsRecourse('Responsabilité en tant que propriétaire à l’égard du locataire'),
    neighboursAndThirdPartiesLiability,
  ],
})

const vehicleDamageCover = 'Incendie, vol, dommages tous accidents, attentats et émeutes, catastrophes naturelles'

export const frMrgaLimitOfIndemnityByPolicyItem_v1_0_0 = {
  publicLiability: {
    limit: {
      type: 'multipleLimits',
      limits: [
        {
          label: 'Par année d’assurance',
          limit: { type: 'fixedAmount', value: fixedLimitsOfIndemnity.publicLiabilityYearly },
        },
        { label: 'Par sinistre', limit: { type: 'fixedAmount', value: newAmount(2_500_000) } },
      ],
    },
    additionalInfo: 'Détail des sous-garanties aux Conditions particulières',
  },
  productsAndCompletedWorksLiability: {
    limit: { type: 'fixedAmount', amount: newAmount(3_000_000) },
    additionalInfo: 'Par année d’assurance',
  },
  fire: { select: fireLimit, additionalInfo: null },
  electricalDamage: {
    select: (state) => contentCapitalShare({ state, rate: '0.1' }),
    additionalInfo: 'Contenu : 10 % du montant de la garantie Incendie',
  },
  climateEvents: {
    select: (state) => contentCapitalShare({ state, rate: '0.2' }),
    additionalInfo: 'Contenu : 20 % du montant de la garantie Incendie',
  },
  naturalDisasters: { select: fireLimit, additionalInfo: null },
  terrorism: {
    select: (state) => contentCapitalShare({ state, rate: '0.1' }),
    additionalInfo: 'Contenu : 10 % du montant de la garantie Incendie',
  },
  waterDamage: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        { label: 'Contenu', limit: contentCapitalShare({ state, rate: '0.5' }) },
        { label: 'Frais de recherche de fuite', limit: { type: 'fixedAmount', value: newAmount(5000) } },
        {
          label: 'Frais de réparation des conduites et appareils détériorés par le gel',
          limit: { type: 'fixedAmount', value: newAmount(5000) },
        },
        fittingsFinancialLoss(state),
        rentalRisks('Risques locatifs'),
        tenantsRecourse('Recours des locataires'),
        neighboursAndThirdPartiesLiability,
      ],
    }),
    additionalInfo: null,
  },
  buildingDamageAfterTheft: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        {
          label: 'Frais de gardiennage et de clôture provisoire',
          limit: { type: 'fixedAmount', value: newAmount(3000) },
        },
        { label: 'Frais de remplacement des clés et serrures', limit: { type: 'fixedAmount', value: newAmount(3000) } },
        fittingsFinancialLoss(state),
        expertFeesOfDirectDamage(10),
      ],
    }),
    additionalInfo: null,
  },
  theft: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        { label: 'Contenu', limit: contentCapitalShare({ state, rate: '0.5' }) },
        {
          label: 'Dont supports d’information non informatiques',
          limit: { type: 'fixedAmount', value: newAmount(5000) },
        },
        { label: 'Dont espèces monnayées et valeurs', limit: { type: 'fixedAmount', value: newAmount(2500) } },
        {
          label: 'Dont biens en dépendances sans communication',
          limit: { type: 'fixedAmount', value: newAmount(3500) },
        },
        { label: 'Dont clés et serrures', limit: { type: 'fixedAmount', value: newAmount(1500) } },
        expertFeesOfDirectDamage(5),
        {
          label: 'Frais de gardiennage et de clôture provisoire',
          limit: { type: 'fixedAmount', value: newAmount(3000) },
        },
      ],
    }),
    additionalInfo: 'Le non-respect des mesures de protection des Conditions particulières prive de la garantie',
  },
  glass: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        { label: 'Produits verriers', limit: contentCapitalShare({ state, rate: '0.2' }) },
        {
          label: 'Enseignes lumineuses, décorations, biens mobiliers, dégâts immobiliers',
          limit: { type: 'fixedAmount', value: newAmount(3000) },
        },
        {
          label: 'Frais de pose, de dépose et de transport liés à la remise en état',
          limit: { type: 'fixedAmount', value: newAmount(1500) },
        },
        {
          label: 'Frais de clôture provisoire et de gardiennage',
          limit: { type: 'fixedAmount', value: newAmount(3000) },
        },
      ],
    }),
    additionalInfo: null,
  },
  // The agreed terms word the limit as a share of the furniture and goods, which the content capital does not
  // single out: it is not computed in euros
  machineryAndComputerBreakage: {
    limit: {
      type: 'multipleLimits',
      limits: [
        {
          label: 'Dommages matériels aux biens garantis',
          limit: { type: 'text', value: '20 % du mobilier et des marchandises' },
        },
        expertFeesOfDirectDamage(5),
        {
          label: 'Frais de reconstitution des informations, frais supplémentaires d’exploitation',
          limit: { type: 'text', value: '50 % de la somme assurée au titre des dommages matériels aux biens garantis' },
        },
        {
          label: 'Frais de déplacement et de réinstallation, frais de retirement et de déblaiement',
          limit: { type: 'text', value: '5 % de la somme assurée au titre des dommages matériels aux biens garantis' },
        },
      ],
    },
    additionalInfo: null,
  },
  businessInterruption: {
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Période d’indemnisation', limit: { type: 'timesInMonths', value: 12 } },
        {
          label: 'Indemnisation',
          limit: { type: 'text', value: 'Perte de marge brute, dans la limite de 50 % du chiffre d’affaires' },
        },
      ],
    },
    additionalInfo: null,
  },
  civilLiability: {
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Dommages corporels', limit: { type: 'text', value: 'Illimité' } },
        {
          label: 'Dommages matériels',
          limit: { type: 'fixedAmount', value: fixedLimitsOfIndemnity.civilLiabilityPropertyDamage },
        },
      ],
    },
    additionalInfo: 'Véhicules propriété et véhicules confiés',
  },
  criminalDefenseAndRecourse: {
    limit: { type: 'fixedAmount', amount: newAmount(3000) },
    additionalInfo: 'Par litige et par année d’assurance',
  },
  driverProtection: {
    limit: {
      type: 'multipleLimits',
      limits: [
        {
          label: '4 roues',
          limit: { type: 'fixedAmount', value: fixedLimitsOfIndemnity.driverProtectionFourWheels },
        },
        { label: '2 roues', limit: { type: 'fixedAmount', value: fixedLimitsOfIndemnity.driverProtectionTwoWheels } },
      ],
    },
    additionalInfo: null,
  },
  ownedVehiclesDamage: {
    limit: { type: 'fixedAmount', amount: newAmount(45_000) },
    additionalInfo: `Par véhicule : ${vehicleDamageCover}`,
  },
  personalBelongingsAndAccessories: {
    limit: { type: 'fixedAmount', amount: newAmount(3000) },
    additionalInfo: null,
  },
  vehiclesForSaleDamage: {
    limit: { type: 'fixedAmount', amount: newAmount(45_000) },
    additionalInfo: `Par véhicule : ${vehicleDamageCover}`,
  },
  entrustedVehiclesDamage: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        { label: 'Par véhicule', limit: { type: 'fixedAmount', value: newAmount(45_000) } },
        { label: 'Limite contractuelle d’indemnité', limit: chosenAmountLimit(state.frMrgaVehiclesLimitOfIndemnity) },
      ],
    }),
    additionalInfo: `Par véhicule : ${vehicleDamageCover}, forces de la nature`,
  },
  legalProtection: {
    limit: {
      type: 'multipleLimits',
      limits: [
        { label: 'Plafond global par sinistre', limit: { type: 'fixedAmount', value: newAmount(40_000) } },
        { label: 'Gestion amiable (avocat et expert)', limit: { type: 'fixedAmount', value: newAmount(2500) } },
        { label: 'Frais d’expertise judiciaire', limit: { type: 'fixedAmount', value: newAmount(5000) } },
        { label: 'Honoraires d’avocat et de procédure', limit: { type: 'text', value: 'Selon barème' } },
        {
          label: 'Litige hors France, Monaco, Andorre, Union européenne, Royaume-Uni et Suisse',
          limit: { type: 'fixedAmount', value: newAmount(3500) },
        },
        {
          label: 'Garantie fiscale et sociale, par contrôle',
          limit: { type: 'fixedAmount', value: newAmount(10_000) },
        },
        { label: 'Médiation de la consommation, par an', limit: { type: 'fixedAmount', value: newAmount(2000) } },
        { label: 'Usurpation d’identité, par sinistre', limit: { type: 'fixedAmount', value: newAmount(1500) } },
      ],
    },
    additionalInfo:
      'Le plafond global ne se reconstitue pas. Délai de carence de 2 mois sur la garantie fiscale et sociale',
  },
} as const satisfies Record<FrMrgaPolicyItem, FrMrgaLimitOfIndemnityEntry>
